import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Section, SectionHeader } from "@/components/Section";
import { GALLERY, GALLERY_CATEGORIES } from "@/lib/site";

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  return (
    <div data-testid="gallery-page">
      <Seo
        title="Project Gallery"
        description="Recent bathroom, kitchen, tile, painting and construction projects by IVrenovation in the Durham Region and the GTA."
      />
      <Section>
        <SectionHeader
          overline="Project Gallery"
          title="Real homes. Real craftsmanship."
          description="A selection of recently completed bathroom, kitchen, tile, painting and construction projects."
        />

        <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
          {GALLERY_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
              className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border transition ${
                filter === c
                  ? "bg-zinc-950 text-white border-zinc-950"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div
          layout
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
        >
          <AnimatePresence>
            {filtered.map((g, idx) => (
              <motion.button
                layout
                key={g.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35, delay: idx * 0.03 }}
                onClick={() => setLightbox(g)}
                className="group relative overflow-hidden border border-zinc-200 aspect-[4/3] bg-zinc-100 text-left"
                data-testid={`gallery-item-${g.id}`}
              >
                <img
                  src={g.image}
                  alt={g.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 p-5 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                  <p className="text-xs uppercase tracking-wider text-orange-400 font-bold">{g.category}</p>
                  <p className="font-display text-white text-xl tracking-tight">{g.title}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </Section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-zinc-950/90 backdrop-blur-md z-[60] flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white p-2 hover:text-orange-500"
              onClick={() => setLightbox(null)}
              data-testid="lightbox-close"
            >
              <X className="w-7 h-7" />
            </button>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.image} alt={lightbox.title} className="w-full max-h-[80vh] object-contain border border-zinc-700" />
              <div className="mt-4">
                <p className="overline" style={{ color: "#fb923c" }}>{lightbox.category}</p>
                <p className="font-display text-3xl text-white tracking-tight mt-1">{lightbox.title}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
